import { isApiError } from '@pa/api'
import { getGetClinicQueryKey, getListClinicsQueryKey, useUpdateClinic, type ClinicDetail } from '@pa/api/admin'
import { toastApiError } from '@pa/staff'
import { CloseButton, Modal, Select, useToast } from '@pa/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useId, useMemo, useState, type SyntheticEvent } from 'react'
import { messageFor, timeZoneOptions } from '@/features/clinics'

/** S-04 Edit (UserFlows S-04; API.md §7.5): name and time zone. The Clinic ID can't change (D-41). */
export function EditClinicModal({ clinic, onClose }: { clinic: ClinicDetail; onClose: () => void }) {
  const id = useId()
  const toast = useToast()
  const queryClient = useQueryClient()
  const update = useUpdateClinic()
  const zones = useMemo(() => {
    const options = timeZoneOptions()
    // A stored zone is always offered, even if this browser lists it under another name.
    return options.some((o) => o.value === clinic.timeZone)
      ? options
      : [{ value: clinic.timeZone, label: clinic.timeZone, group: 'Current' }, ...options]
  }, [clinic.timeZone])
  const [name, setName] = useState(clinic.name)
  const [timeZone, setTimeZone] = useState(clinic.timeZone)
  const [errors, setErrors] = useState<{ name?: string; timeZone?: string }>({})

  async function submit(e: SyntheticEvent) {
    e.preventDefault()
    const trimmed = name.trim()
    if (trimmed.length < 1 || trimmed.length > 120) {
      setErrors({ name: messageFor('name_length') })
      return
    }
    const data = {
      ...(trimmed !== clinic.name && { name: trimmed }),
      ...(timeZone !== clinic.timeZone && { timeZone }),
    }
    if (Object.keys(data).length === 0) {
      onClose()
      return
    }
    try {
      const updated = await update.mutateAsync({ clinicId: clinic.id, data })
      queryClient.setQueryData(getGetClinicQueryKey(clinic.id), updated)
      void queryClient.invalidateQueries({ queryKey: getListClinicsQueryKey() })
      toast('Clinic updated')
      onClose()
    } catch (err) {
      if (isApiError(err) && err.code === 'invalid_request' && err.issues?.length) {
        const next: { name?: string; timeZone?: string } = {}
        for (const issue of err.issues) {
          if (issue.path[0] === 'name') next.name = messageFor(issue.code)
          if (issue.path[0] === 'timeZone') next.timeZone = messageFor(issue.code)
        }
        if (next.name ?? next.timeZone) {
          setErrors(next)
          return
        }
      }
      toastApiError(toast, err)
    }
  }

  return (
    <Modal onClose={onClose} labelledBy={`${id}-title`}>
      <form onSubmit={(e) => void submit(e)} noValidate>
        <div className="modal-head">
          <div>
            <h2 id={`${id}-title`}>Edit clinic</h2>
            <p className="muted">
              The Clinic ID <span className="mono">{clinic.slug}</span> can&apos;t be changed.
            </p>
          </div>
          <CloseButton onClick={onClose} />
        </div>
        <div className="modal-body form-grid">
          <div className="field">
            <label className="label" htmlFor={`${id}-name`}>
              Clinic name
            </label>
            <input
              id={`${id}-name`}
              className="input"
              value={name}
              aria-invalid={errors.name ? true : undefined}
              aria-describedby={errors.name ? `${id}-name-error` : undefined}
              onChange={(e) => {
                setName(e.target.value)
              }}
            />
            {errors.name && (
              <span className="err-text" id={`${id}-name-error`}>
                {errors.name}
              </span>
            )}
          </div>
          <div className="field">
            <span className="label" id={`${id}-tz-label`}>
              Time zone
            </span>
            <Select labelledBy={`${id}-tz-label`} value={timeZone} options={zones} searchable onChange={setTimeZone} />
            {errors.timeZone && <span className="err-text">{errors.timeZone}</span>}
            <span className="hint">
              A new time zone applies to future time slots; booked appointments keep their times.
            </span>
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
